import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, getDocs, doc, getDoc, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getProfilePlaceholder } from '../../utils/placeholders';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';

const ContentApproval = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { currentUser } = useAuth();


  useEffect(() => {
    // Fetch all review submissions from Firestore once — filter/sort client-side
    const fetchSubmissions = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);

        
        // Query proofs collection first (new system), then reviews (backward compatibility)
        const proofsRef = collection(db, 'proofs');
        const reviewsRef = collection(db, 'reviews');
        
        let proofsQuery = query(proofsRef, where('brandId', '==', currentUser.uid));
        let reviewsQuery = query(reviewsRef, where('brandId', '==', currentUser.uid));
        
        // Try to apply sorting, but don't fail if index doesn't exist
        let proofsSnapshot, reviewsSnapshot;
        
        try {
          proofsQuery = query(proofsQuery, orderBy('submittedAt', 'desc'));
          reviewsQuery = query(reviewsQuery, orderBy('submittedAt', 'desc'));
          
          // Fetch from both collections
          [proofsSnapshot, reviewsSnapshot] = await Promise.all([
            getDocs(proofsQuery),
            getDocs(reviewsQuery)
          ]);
        } catch (indexError) {
          console.warn('Sorting failed (index may not exist), fetching without sorting:', indexError);
          
          // Retry without orderBy
          proofsQuery = query(proofsRef, where('brandId', '==', currentUser.uid));
          reviewsQuery = query(reviewsRef, where('brandId', '==', currentUser.uid));
          
          [proofsSnapshot, reviewsSnapshot] = await Promise.all([
            getDocs(proofsQuery),
            getDocs(reviewsQuery)
          ]);
        }
        
        // Combine results
        const allDocs = [...proofsSnapshot.docs, ...reviewsSnapshot.docs];

        
        const submissionsData = [];
        
        // Process each proof/review document
        for (const docSnapshot of allDocs) {
          const reviewData = docSnapshot.data();
          
          // Get campaign details
          let campaignName = reviewData.campaignTitle || 'Unknown Campaign';
          if (reviewData.campaignId && !campaignName) {
            try {
              const campaignDoc = await getDoc(doc(db, 'campaigns', reviewData.campaignId));
              if (campaignDoc.exists()) {
                campaignName = campaignDoc.data().title || campaignDoc.data().name || 'Unnamed Campaign';
              }
            } catch (error) {
              console.error('Error fetching campaign details:', error);
            }
          }
          
          // Get influencer details if needed
          let influencerName = reviewData.influencerName || 'Unknown Influencer';
          let influencerAvatar = reviewData.influencerProfileImage || getProfilePlaceholder(reviewData.influencerName || 'User');
          
          if (reviewData.influencerId && (!reviewData.influencerName || !reviewData.influencerProfileImage)) {
            try {
              const influencerDoc = await getDoc(doc(db, 'users', reviewData.influencerId));
              if (influencerDoc.exists()) {
                const influencerData = influencerDoc.data();
                influencerName = influencerData.displayName || influencerData.firstName + ' ' + influencerData.lastName || influencerName;
                influencerAvatar = influencerData.profileImageUrl || influencerAvatar;
              }
            } catch (error) {
              console.error('Error fetching influencer details:', error);
            }
          }
          
          // Check if this is a resubmission
          const isResubmission = reviewData.resubmittedAt ? true : false;
          const resubmissionDate = reviewData.resubmittedAt ? new Date(reviewData.resubmittedAt.toDate()).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : null;
          
          // Format the submission data
          submissionsData.push({
            id: docSnapshot.id,
            campaignId: reviewData.campaignId || '',
            campaignName: campaignName,
            influencerId: reviewData.influencerId || '',
            influencerName: influencerName,
            influencerUsername: reviewData.influencerEmail || '',
            influencerAvatar: influencerAvatar,
            contentType: reviewData.postUrl ? 'Proof' : 'Review',
            submittedDate: reviewData.submittedAt ? new Date(reviewData.submittedAt.toDate()).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown',
            status: reviewData.status || 'pending',
            content: (() => {
              // For proof submissions, check screenshotUrls or contentUrl
              if (reviewData.screenshotUrls && reviewData.screenshotUrls.length > 0) {
                return reviewData.screenshotUrls[0];
              }
              if (reviewData.contentUrl) {
                return reviewData.contentUrl;
              }
              // For review submissions, check imageUrls
              if (reviewData.imageUrls && reviewData.imageUrls.length > 0) {
                return reviewData.imageUrls[0];
              }
              // Fallback to empty string
              return '';
            })(),
            caption: reviewData.reviewText || '',
            rating: reviewData.rating || 0,
            pros: reviewData.pros || [],
            cons: reviewData.cons || [],
            contentLinks: reviewData.contentLinks || [],
            notes: reviewData.brandFeedback || '',
            feedbackRequired: reviewData.status === 'rejected',
            isResubmission: isResubmission,
            resubmissionDate: resubmissionDate
          });
        }
        
        setSubmissions(submissionsData);

      } catch (error) {
        console.error('Error fetching submissions:', error);
        setError('Failed to load content submissions. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, [currentUser]);

  const getFilteredSubmissions = () => {
    let filtered = [...submissions];

    if (filter !== 'all') {
      filtered = filtered.filter(sub => sub.status === filter);
    }

    if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.submittedDate) - new Date(a.submittedDate));
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.submittedDate) - new Date(b.submittedDate));
    }

    return filtered;
  };
  
  // Pagination logic
  const filteredSubmissions = getFilteredSubmissions();
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSubmissions = filteredSubmissions.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, sortBy]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            Need Revision
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
    }
  };

  const formatDate = (date) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    
    if (!date) return 'N/A';
    
    try {
      // Handle Firebase Timestamp objects
      if (date && typeof date.toDate === 'function') {
        return date.toDate().toLocaleDateString(undefined, options);
      }
      
      // Handle Date objects
      if (date instanceof Date) {
        return date.toLocaleDateString(undefined, options);
      }
      
      // Handle string timestamps
      return new Date(date).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Something went wrong</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>

      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Content Approval</h2>
            <p className="text-gray-600">Review and approve content submissions from influencers</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 flex flex-col sm:flex-row justify-between items-center border-b border-gray-200">
          <div className="flex space-x-2 mb-4 sm:mb-0">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                filter === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                filter === 'pending'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter('approved')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                filter === 'approved'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Approved
            </button>
            <button
              onClick={() => setFilter('rejected')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                filter === 'rejected'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Rejected
            </button>
          </div>

          <div className="relative">
            <Select
              value={sortBy}
              onValueChange={(value) => setSortBy(value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Submissions List */}
        <div className="overflow-hidden">
          {filteredSubmissions.length > 0 ? (
            <>
              <ul className="divide-y divide-gray-200">
                {paginatedSubmissions.map((submission) => (
                  <li key={submission.id} className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center">
                      <div className="flex-shrink-0 mr-4 mb-4 md:mb-0">
                        <img
                          src={submission.content}
                          alt="Content preview"
                          className="h-24 w-24 object-cover rounded-md"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f3f4f6" width="100" height="100"/%3E%3Ctext fill="%236b7280" font-family="Arial" font-size="12" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EContent%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center mb-1">
                        <img
                          src={submission.influencerAvatar}
                          alt={submission.influencerName}
                          className="h-5 w-5 rounded-full mr-1"
                        />
                        <p className="text-sm font-medium text-gray-900">
                          {submission.influencerName}{' '}
                          <span className="font-normal text-gray-500">{submission.influencerUsername}</span>
                        </p>
                      </div>
                      <p className="text-sm text-gray-500 mb-1 truncate">
                        Campaign: {submission.campaignName}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {getStatusBadge(submission.status)}
                        {submission.isResubmission && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                            Resubmission
                          </span>
                        )}
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                          {submission.contentType === 'Proof' ? 'Proof Submission' : 'Review Submission'}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {submission.contentType}
                        </span>
                        <span className="text-xs text-gray-500">
                          {submission.isResubmission ? `Resubmitted: ${submission.resubmissionDate}` : `Submitted: ${formatDate(submission.submittedDate)}`}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-1">{submission.caption}</p>
                    </div>
                    <div className="flex-shrink-0 mt-4 md:mt-0 md:ml-4">
                      <Link
                        to={`/brand/content-approval/${submission.id}`}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
              </ul>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex + 1}</span> to <span className="font-medium">{Math.min(endIndex, filteredSubmissions.length)}</span> of{' '}
                        <span className="font-medium">{filteredSubmissions.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => setCurrentPage(pageNumber)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  currentPage === pageNumber
                                    ? 'z-10 bg-primary border-primary text-white'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          } else if (
                            pageNumber === currentPage - 2 ||
                            pageNumber === currentPage + 2
                          ) {
                            return <span key={pageNumber} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                          }
                          return null;
                        })}
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-8 text-center">
              <p className="text-gray-500">No submissions found matching your filters.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentApproval;
